import styled from '@emotion/styled'
import { AxeEvent } from 'api'
import { AxeActions } from 'api/AxeAction'
import { Locale, Translation } from 'locale'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AxeDetail } from 'selectors/axeSelectors'
import { createCollateralsFromInstrumentIdGetterSelector } from 'selectors/instrumentSelectors'
import { isNotNil } from 'utils/predicate'
import { ActionsButtonProps } from 'view/components/datagrid'
import { ActionItem, SplitButtonDropdown, SplitButtonType } from 'view/components/menus'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { canPerformEvent } from './axeModelUtils'
import { AxeStageModal } from './AxeStageModal'

const AxeActionsButton = styled(SplitButtonDropdown)`
  width: 200px;
`

const AxeActionsButtonContainer: React.FC<ActionsButtonProps<AxeDetail>> = withModal<
  ActionsButtonProps<AxeDetail> & ModalConsumerProps,
  unknown
>(({ items: axeDetails, showModal, dataTestId }) => {
  const dispatch = useDispatch()
  const getCollateralsFromInstrumentId = useSelector(createCollateralsFromInstrumentIdGetterSelector())

  const handleSend = () => axeDetails.forEach(axeDetail => dispatch(AxeActions.send({ axeId: axeDetail.axe.axeId })))
  const handleCancel = () =>
    axeDetails.forEach(axeDetail => dispatch(AxeActions.cancel({ axeId: axeDetail.axe.axeId, externalAxeCancelId: null })))
  const handleClone = () => axeDetails.forEach(axeDetail => dispatch(AxeActions.clone({ axeId: axeDetail.axe.axeId })))

  const handleStage = () => {
    const {
      axe: { axeId },
      instrument
    } = axeDetails[0]
    if (isNotNil(axeId) && isNotNil(instrument)) {
      showModal(<AxeStageModal axeDetails={axeDetails} />)
    }
  }

  const getLabel = (translation: Translation) => {
    const count = axeDetails.length
    return `${Locale.translate(translation)} ${count > 1 ? '(' + count + ')' : ''}`
  }

  const allAxeDetailsCan = (event: AxeEvent) => axeDetails.length > 0 && axeDetails.every(a => canPerformEvent(a, event))

  const actions: ActionItem[] = [
    {
      label: getLabel(Translation.TC_AXE_SEND),
      onClick: handleSend,
      type: SplitButtonType.Good,
      enabled: allAxeDetailsCan(AxeEvent.SEND)
    },
    {
      label: getLabel(Translation.TC_AXE_CANCEL),
      onClick: handleCancel,
      type: SplitButtonType.Bad,
      enabled: allAxeDetailsCan(AxeEvent.CANCEL)
    },
    {
      label: getLabel(Translation.TC_AXE_CLONE),
      onClick: handleClone,
      enabled: allAxeDetailsCan(AxeEvent.CLONE)
    },
    {
      label: getLabel(Translation.TC_AXE_STAGE),
      onClick: handleStage,
      enabled: (() => {
        const someAreBasket = axeDetails.some(ad => {
          if (ad.instrument) {
            const { collateralBasket } = getCollateralsFromInstrumentId(ad.instrument.id)
            return isNotNil(collateralBasket)
          }
          return false
        })

        if (someAreBasket && axeDetails.length > 1) {
          return false
        }

        return allAxeDetailsCan(AxeEvent.STAGE)
      })() // check for collateral mix
    }
  ]

  return <AxeActionsButton dataTestId={dataTestId ?? 'AxeActions'} actions={actions} maxButtons={1} />
})

export { AxeActionsButtonContainer as AxeActionsButton }
